import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';

const CATEGORY_LABELS = {
  beta:       'Beta Features',
  billing:    'Paid Features',
  compliance: 'Compliance',
  platform:   'Platform Defaults',
};

export function FeatureAdminPanel({ tenantId }) {
  const { user } = useAuth();
  const { makeRequest } = useApiCall();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null); // feature key currently saving
  const [error, setError]       = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    makeRequest(
      () => apiService.get(`/api/v1/tenants/${tenantId}/features`, user),
      'Loading features...'
    )
      .then(data => setFeatures(data.features || []))
      .catch(err => setError(err.message || 'Failed to load features'))
      .finally(() => setLoading(false));

  }, [tenantId]);

  async function handleToggle(featureKey, currentEnabled) {
    setSaving(featureKey);
    try {
      await makeRequest(
        () => apiService.patch(
          `/api/v1/tenants/${tenantId}/features/${featureKey}`,
          { enabled: !currentEnabled },
          user
        )
      );
      setFeatures(prev =>
        prev.map(f => f.key === featureKey ? { ...f, enabled: !currentEnabled } : f)
      );
    } catch (err) {
      setError(`Failed to update ${featureKey}: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(null);
    }
  }

  if (loading) {
    return (
      <div className="loading-dots" style={{ padding: 'var(--spacing-4)' }}>
        <span className="loading-dot" />
        <span className="loading-dot" />
        <span className="loading-dot" />
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  // Group by category
  const grouped = features.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="feature-admin-panel">
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category} style={{ marginBottom: 'var(--spacing-6)' }}>
          <h4 style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-gray-500)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: '0 0 var(--spacing-3) 0'
          }}>
            {CATEGORY_LABELS[category] ?? category}
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--spacing-2) var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', fontWeight: 500 }}>Feature</th>
                <th style={{ textAlign: 'left', padding: 'var(--spacing-2) var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', fontWeight: 500 }}>Description</th>
                <th style={{ textAlign: 'center', padding: 'var(--spacing-2) var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', fontWeight: 500, width: '100px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.key} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                  <td style={{ padding: 'var(--spacing-3)', verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-800)' }}>
                      {f.display_name}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', fontFamily: 'monospace', marginTop: '2px' }}>
                      {f.key}
                    </div>
                  </td>
                  <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', verticalAlign: 'top' }}>
                    {f.description}
                  </td>
                  <td style={{ padding: 'var(--spacing-3)', textAlign: 'center', verticalAlign: 'top' }}>
                    <button
                      className={`btn btn-sm ${f.enabled ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => handleToggle(f.key, f.enabled)}
                      disabled={saving === f.key}
                      aria-pressed={f.enabled}
                    >
                      {saving === f.key ? 'Saving…' : f.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
